import { toCalendarDate } from '@/utils'
import type { FieldSchema, Filter } from './types'

const normalizeDate = (dateInput: string | Date): string =>
  toCalendarDate(dateInput)

export function applyFilter(
  value: any,
  filter: Filter,
  fieldSchema: FieldSchema,
): boolean {
  const { operator, value: filterValue, value2 } = filter
  const { type } = fieldSchema

  if (type === 'select') {
    if (operator === 'in') {
      if (Array.isArray(filterValue)) {
        return filterValue.some((fv) => String(fv) === String(value))
      }
      return String(value) === String(filterValue)
    }
    if (operator === 'not_in') {
      if (Array.isArray(filterValue)) {
        return !filterValue.some((fv) => String(fv) === String(value))
      }
      return String(value) !== String(filterValue)
    }
    return true
  }

  if (type === 'date') {
    const dateValue = normalizeDate(value)
    const filterDate = normalizeDate(filterValue)

    switch (operator) {
      case 'equals':
        return dateValue === filterDate
      case 'not_equals':
        return dateValue !== filterDate
      case 'before':
        return dateValue < filterDate
      case 'after':
        return dateValue > filterDate
      case 'between':
        return dateValue >= filterDate && dateValue <= normalizeDate(value2)
      default:
        return true
    }
  }

  if (type === 'number') {
    const numValue = parseFloat(value)
    const filterNum = parseFloat(filterValue)

    switch (operator) {
      case 'equals':
        return numValue === filterNum
      case 'not_equals':
        return numValue !== filterNum
      case 'greater_than':
        return numValue > filterNum
      case 'greater_than_or_equal':
        return numValue >= filterNum
      case 'less_than':
        return numValue < filterNum
      case 'less_than_or_equal':
        return numValue <= filterNum
      case 'between':
        return numValue >= filterNum && numValue <= parseFloat(value2)
      default:
        return true
    }
  }

  if (type === 'text') {
    const textValue = String(value).toLowerCase()
    const filterText = String(filterValue).toLowerCase()

    switch (operator) {
      case 'contains':
        return textValue.includes(filterText)
      case 'not_contains':
        return !textValue.includes(filterText)
      case 'equals':
        return textValue === filterText
      case 'not_equals':
        return textValue !== filterText
      case 'starts_with':
        return textValue.startsWith(filterText)
      case 'ends_with':
        return textValue.endsWith(filterText)
      default:
        return true
    }
  }

  if (type === 'boolean') {
    const boolValue = value === true || value === 'true'
    const filterBool = filterValue === true || filterValue === 'true'

    switch (operator) {
      case 'equals':
        return boolValue === filterBool
      default:
        return true
    }
  }

  return true
}

export const applyFilters = (
  data: any[],
  filters: Filter[],
  schema: FieldSchema[],
): any[] => {
  if (!filters || filters.length === 0) return data

  return data.filter((item) => {
    return filters.every((filter) => {
      const fieldSchema = schema.find(
        (fieldDef) => fieldDef.field === filter.field,
      )
      if (!fieldSchema) return true

      const value = item[filter.field]
      return applyFilter(value, filter, fieldSchema)
    })
  })
}

export const createTableFilterFn = (schema: FieldSchema[]) => {
  return (row: any, columnId: string, filterValue: any): boolean => {
    if (!filterValue) return true

    const fieldSchema = schema.find((s) => s.field === columnId)
    if (!fieldSchema) return true

    const value = row.getValue(columnId)
    return applyFilter(value, filterValue, fieldSchema)
  }
}
